import { Flooding } from 'features/flooding/flooding.types';
import { GeoEventUrgencyLevel } from 'features/geoEvents/geoEvent.types';
import { ReporterNoteToggle } from 'features/reporterNotes/reporterNotes.types';
import { hasReporterNotesChanges } from 'features/reporterNotes/reporterNotes.utils';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { hasReporterOnlyNotesChanged } from './useGeoEventMetadataFormFieldChanges.utils';

type UseGeoEventMetadataFormFieldChangesProps = {
  geoEvent: Wildfire | Flooding;
};

type FormValues = {
  temporarilyDisplayEvacZones: boolean;
  reporterOnlyNotes: string;
  urgency: GeoEventUrgencyLevel;
  manualDeactivationStarted: boolean | null;
  reporterManaged: boolean | undefined;
  newReporterNote?: string;
  reporterNoteToggles?: ReporterNoteToggle[];
};

type ChangeKey =
  | 'urgency'
  | 'reporterOnlyNotes'
  | 'temporarilyDisplayEvacZones'
  | 'manualDeactivationStarted'
  | 'reporterManaged';

type ChangeMap = Partial<Record<ChangeKey, string>>;

export const useGeoEventMetadataFormFieldChanges = (
  props: UseGeoEventMetadataFormFieldChangesProps,
): ChangeMap => {
  const { geoEvent } = props;
  const { t } = useTranslation();

  const [
    temporarilyDisplayEvacZones,
    reporterOnlyNotes,
    urgency,
    manualDeactivationStarted,
    reporterManaged,
    newReporterNote,
    reporterNoteToggles,
  ] = useWatch<FormValues>({
    name: [
      'temporarilyDisplayEvacZones',
      'reporterOnlyNotes',
      'urgency',
      'manualDeactivationStarted',
      'reporterManaged',
      'newReporterNote',
      'reporterNoteToggles',
    ],
  }) as [
    boolean,
    string,
    GeoEventUrgencyLevel,
    boolean | null,
    boolean | undefined,
    string | undefined,
    ReporterNoteToggle[] | undefined,
  ];

  const temporarilyDisplayEvacZonesChanged =
    geoEvent.data.temporarilyDisplayEvacZones !== temporarilyDisplayEvacZones;
  const reporterOnlyNotesChanged = hasReporterOnlyNotesChanged(
    geoEvent.data.reporterOnlyNotes,
    reporterOnlyNotes,
  );

  const reporterNotesChanged = hasReporterNotesChanges({
    newReporterNote: newReporterNote ?? '',
    reporterNoteToggles: reporterNoteToggles ?? [],
  });

  // Urgency is undefined for the report form, but the field is common to wildfire and flooding forms.
  // Rx incidents have null urgency; the form defaults null → Standard, so guard against treating that as a change.
  const urgencyChanged =
    Boolean(urgency) &&
    geoEvent.data.urgency != null &&
    geoEvent.data.urgency !== urgency;
  const manualDeactivationStartedChanged =
    Boolean(geoEvent.data.manualDeactivationStarted) !==
    Boolean(manualDeactivationStarted);
  const reporterManagedChanged =
    reporterManaged != null && geoEvent.reporterManaged !== reporterManaged;

  const changes: ChangeMap = {};
  if (urgencyChanged) {
    changes.urgency = t('geoEventChangelog.urgency', {
      prevValue: t(`geoEvent.urgency.${geoEvent.data.urgency}`),
      newValue: t(`geoEvent.urgency.${urgency}`),
    });
  }
  if (reporterOnlyNotesChanged || reporterNotesChanged) {
    changes.reporterOnlyNotes = t('geoEventChangelog.reporterOnlyNotes');
  }
  if (temporarilyDisplayEvacZonesChanged) {
    changes.temporarilyDisplayEvacZones = t(
      'geoEventChangelog.temporarilyDisplayEvacZones',
    );
  }
  if (manualDeactivationStartedChanged) {
    changes.manualDeactivationStarted = t('editGeoEvent.startDeactivation');
  }
  if (reporterManagedChanged) {
    changes.reporterManaged = t(
      reporterManaged
        ? 'geoEventChangelog.reporterManagedEnabled'
        : 'geoEventChangelog.reporterManagedDisabled',
    );
  }

  return changes;
};
